import { useState, useEffect } from 'react'
import { Plus, Trash2, Copy, Eye, EyeOff, AlertCircle, CheckCircle, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { apiClient } from '../../services/api'

interface APIToken {
  id: number
  name: string
  token_prefix: string
  scope: string
  description: string | null
  is_active: boolean
  expires_at: string | null
  last_used_at: string | null
  created_at: string
}

interface CreateForm {
  name: string
  scope: 'read' | 'write' | 'admin'
  description: string
  expires_at: string
}

const SCOPE_LABELS: Record<string, { label: string; color: string; description: string }> = {
  read: {
    label: 'Read-only',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    description: 'Read project data, insights, milestones',
  },
  write: {
    label: 'Write',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    description: 'Update task statuses, create resources',
  },
  admin: {
    label: 'Admin',
    color: 'bg-red-100 text-red-700 border-red-300',
    description: 'Full access including destructive operations',
  },
}

export function APITokensSection() {
  const [tokens, setTokens] = useState<APIToken[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [rawToken, setRawToken] = useState<string | null>(null)
  const [tokenVisible, setTokenVisible] = useState(false)
  const [form, setForm] = useState<CreateForm>({ name: '', scope: 'read', description: '', expires_at: '' })
  const [formErrors, setFormErrors] = useState<Partial<CreateForm>>({})

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<APIToken[]>('/api/v1/api-tokens/')
      setTokens(res.data)
    } catch {
      toast.error('Failed to load API tokens')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const validate = (): boolean => {
    const errs: Partial<CreateForm> = {}
    if (!form.name.trim()) errs.name = 'Name is required'
    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleCreate = async () => {
    if (!validate()) return
    setCreating(true)
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        scope: form.scope,
        description: form.description.trim() || null,
      }
      if (form.expires_at) payload.expires_at = form.expires_at
      const res = await apiClient.post<APIToken & { raw_token: string }>('/api/v1/api-tokens/', payload)
      setRawToken(res.data.raw_token)
      setTokenVisible(false)
      setTokens(prev => [res.data, ...prev])
      setForm({ name: '', scope: 'read', description: '', expires_at: '' })
      setShowCreate(false)
      toast.success('API token created — copy it now, it won\'t be shown again')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Failed to create token')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (id: number, name: string) => {
    if (!confirm(`Revoke token "${name}"? Any integrations using it will immediately stop working.`)) return
    try {
      await apiClient.delete(`/api/v1/api-tokens/${id}`)
      setTokens(prev => prev.filter(t => t.id !== id))
      toast.success('Token revoked')
    } catch {
      toast.error('Failed to revoke token')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
  }

  return (
    <div className="space-y-6">
      {/* Raw token banner — shown once after creation */}
      {rawToken && (
        <div
          role="alert"
          className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 space-y-3"
        >
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <AlertCircle size={18} aria-hidden="true" />
            Save your token — it won't be shown again
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-300 rounded px-3 py-2 text-sm font-mono break-all">
              {tokenVisible ? rawToken : '•'.repeat(40)}
            </code>
            <button
              onClick={() => setTokenVisible(v => !v)}
              aria-label={tokenVisible ? 'Hide token' : 'Show token'}
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg border border-amber-300 hover:bg-amber-100 transition-colors"
            >
              {tokenVisible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
            <button
              onClick={() => copyToClipboard(rawToken)}
              aria-label="Copy token"
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg border border-amber-300 hover:bg-amber-100 transition-colors"
            >
              <Copy size={16} aria-hidden="true" />
            </button>
          </div>
          <button
            onClick={() => setRawToken(null)}
            className="text-sm text-amber-700 underline"
          >
            I've saved it — dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#102a43] text-lg">API Tokens</h3>
          <p className="text-sm text-[#627d98] mt-0.5">
            Tokens authenticate requests to the public REST API and BI connector.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg transition-colors text-sm font-medium"
          aria-expanded={showCreate}
          aria-controls="create-token-form"
        >
          <Plus size={16} aria-hidden="true" />
          New Token
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div
          id="create-token-form"
          className="bg-[#f0f4f8] border-2 border-[#bcccdc] rounded-lg p-5 space-y-4"
          role="region"
          aria-label="Create new API token"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="token-name" className="block text-sm font-medium text-[#334e68] mb-1">
                Name <span className="text-red-600" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                id="token-name"
                type="text"
                value={form.name}
                onChange={e => {
                  setForm(f => ({ ...f, name: e.target.value }))
                  if (formErrors.name) setFormErrors(f => ({ ...f, name: undefined }))
                }}
                onBlur={() => { if (!form.name.trim()) setFormErrors(f => ({ ...f, name: 'Name is required' })) }}
                placeholder="e.g. Power BI connector"
                className={`w-full border-2 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] ${formErrors.name ? 'border-red-400' : 'border-[#bcccdc]'}`}
                aria-describedby={formErrors.name ? 'token-name-error' : undefined}
                aria-required="true"
                aria-invalid={!!formErrors.name}
              />
              {formErrors.name && (
                <p id="token-name-error" className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert">
                  <AlertCircle size={12} aria-hidden="true" />{formErrors.name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="token-scope" className="block text-sm font-medium text-[#334e68] mb-1">
                Scope <span className="text-red-600" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <select
                id="token-scope"
                value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value as CreateForm['scope'] }))}
                className="w-full border-2 border-[#bcccdc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581]"
                aria-required="true"
              >
                {Object.entries(SCOPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label} — {v.description}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="token-desc" className="block text-sm font-medium text-[#334e68] mb-1">Description</label>
              <input
                id="token-desc"
                type="text"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional — what is this token used for?"
                className="w-full border-2 border-[#bcccdc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581]"
              />
            </div>

            <div>
              <label htmlFor="token-expires" className="block text-sm font-medium text-[#334e68] mb-1">
                Expires at <span className="text-[#829ab1] text-xs">(optional)</span>
              </label>
              <input
                id="token-expires"
                type="datetime-local"
                value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                className="w-full border-2 border-[#bcccdc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#486581]"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowCreate(false); setFormErrors({}) }}
              className="px-4 py-2.5 min-h-[44px] border-2 border-[#bcccdc] rounded-lg text-sm text-[#334e68] hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2.5 min-h-[44px] bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create Token'}
            </button>
          </div>
        </div>
      )}

      {/* Token list */}
      {loading ? (
        <p className="text-sm text-[#829ab1] py-4 text-center" role="status">Loading tokens…</p>
      ) : tokens.length === 0 ? (
        <div className="text-center py-10 text-[#829ab1] border-2 border-dashed border-[#bcccdc] rounded-lg">
          <p className="text-sm">No API tokens yet.</p>
          <p className="text-xs mt-1">Create one to authenticate BI tools and Zapier automations.</p>
        </div>
      ) : (
        <ul className="space-y-3" aria-label="API tokens">
          {tokens.map(t => (
            <li
              key={t.id}
              className="flex items-center justify-between p-4 border-2 border-[#bcccdc] rounded-lg bg-white gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-[#102a43]">{t.name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs border font-medium ${SCOPE_LABELS[t.scope]?.color ?? ''}`}>
                    {SCOPE_LABELS[t.scope]?.label ?? t.scope}
                  </span>
                  {!t.is_active && (
                    <span className="px-2 py-0.5 rounded text-xs border bg-gray-100 text-gray-500 border-gray-300">
                      Revoked
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#829ab1] mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span className="font-mono">{t.token_prefix}…</span>
                  <span className="flex items-center gap-1">
                    <Clock size={11} aria-hidden="true" />
                    Created {formatDate(t.created_at)}
                  </span>
                  {t.last_used_at && (
                    <span className="flex items-center gap-1">
                      <CheckCircle size={11} aria-hidden="true" />
                      Last used {formatDate(t.last_used_at)}
                    </span>
                  )}
                  {t.expires_at && (
                    <span>Expires {formatDate(t.expires_at)}</span>
                  )}
                  {t.description && <span className="italic">{t.description}</span>}
                </div>
              </div>
              {t.is_active && (
                <button
                  onClick={() => handleRevoke(t.id, t.name)}
                  aria-label={`Revoke token ${t.name}`}
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Usage guidance */}
      <div className="bg-[#f0f4f8] rounded-lg border border-[#bcccdc] p-4 text-xs text-[#486581] space-y-1">
        <p className="font-semibold">Usage</p>
        <p>Pass the token in the <code className="bg-white px-1 rounded border border-[#bcccdc]">X-API-Key</code> header:</p>
        <pre className="bg-white rounded border border-[#bcccdc] p-2 overflow-x-auto">
{`curl https://api.karyva.io/api/v1/public/projects \\
  -H "X-API-Key: kv_<your-token>"`}
        </pre>
        <p>Power BI / Tableau: use <code className="bg-white px-1 rounded border border-[#bcccdc]">/api/v1/bi/*</code> endpoints with a <strong>read</strong>-scoped token.</p>
      </div>
    </div>
  )
}
